import React, { useCallback, useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radar, ExternalLink, Bot } from "lucide-react";

export const ReceiptRadarWidget = ({ companyId }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/receipt-radar`);
      setData(r.data);
    } catch { /* widget optional */ }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const triggerRobot = async (connectionId) => {
    try {
      await apiClient.post(`/companies/${companyId}/receipt-connections/${connectionId}/fetch-now`);
      toast.info("Roboter-Abruf gestartet – gefundene Belege landen im Posteingang");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abruf fehlgeschlagen");
    }
  };

  if (!data || data.count === 0) return null;

  return (
    <Card className="p-4 border border-amber-200 bg-amber-50/50" data-testid="receipt-radar-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-amber-500 text-white flex items-center justify-center">
            <Radar className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-800" data-testid="receipt-radar-count">
              {data.count} Zahlung{data.count !== 1 ? "en" : ""} ohne Beleg
            </p>
            <p className="text-[11px] text-slate-500">Total CHF {data.total_amount.toFixed(2)} — Belege fehlen für den Abschluss</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/bank-reconciliation")} data-testid="receipt-radar-goto-bank">
          Zum Bankabgleich
        </Button>
      </div>
      <div className="space-y-1" data-testid="receipt-radar-entries">
        {data.entries.slice(0, 4).map((e) => (
          <div key={e.journal_entry_id} className="flex items-center justify-between gap-2 rounded-md bg-white border border-amber-100 px-2.5 py-1.5 text-xs">
            <div className="min-w-0">
              <span className="text-slate-700 truncate">{formatDate(e.date)} — {e.description}</span>
              <span className="ml-2 font-mono text-slate-500">{e.amount.toFixed(2)}</span>
            </div>
            {e.suggestion?.kind === "portal" && (
              <a
                href={e.suggestion.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-indigo-700 hover:underline shrink-0"
                data-testid={`radar-portal-suggestion-${e.journal_entry_id}`}
              >
                <ExternalLink className="h-3 w-3" /> {e.suggestion.name}
              </a>
            )}
            {e.suggestion?.kind === "robot" && (
              <button
                type="button"
                onClick={() => triggerRobot(e.suggestion.connection_id)}
                className="inline-flex items-center gap-1 text-emerald-700 hover:underline shrink-0"
                data-testid={`radar-robot-suggestion-${e.journal_entry_id}`}
              >
                <Bot className="h-3 w-3" /> {e.suggestion.name} abrufen
              </button>
            )}
          </div>
        ))}
        {data.count > 4 && (
          <p className="text-[11px] text-slate-400">… und {data.count - 4} weitere im Bankabgleich.</p>
        )}
      </div>
    </Card>
  );
};
